// src/screens/TeamScreen.js
// ✅ DROP-IN: TeamScreen (STRICT companyId only)
// ✅ Includes: loadTeamUsers fix (fetch ALL company users incl. customers)
// ✅ Includes: accountMap + customer accountName enrichment + basic "Team Members" display on Invites tab
// ✅ FIX: Now properly passes activeUsers, inactiveUsers, and customerUsers to Invites component
// ✅ REMOVED: The 3 boxes (active, inactive, customer) - only invites list on the right remains

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
  limit,
  addDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase";
import { loadCompanyCustomerMemberships } from "../auth/customerMemberships";
import { logAuditEvent } from "../utils/auditLogger";
import { setTeamUserStatus } from "../utils/teamStatus";

import Invites from "../components/team/Invites";
import Groups from "../components/team/Groups";
import Territories from "../components/team/Territories";

import {
  AlertCircle,
  Loader2,
  UserPlus,
  Users,
  Map as MapIcon,
  Gift,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// ------------------------------
// Helpers
// ------------------------------
const normalizeEmail = (email) => (email || "").toString().trim().toLowerCase();

const isValidEmail = (email) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(email));

const normalizeRole = (role) => {
  const r = (role || "").toString().trim().toLowerCase();
  if (["enduser", "end user", "end_user", "end-user"].includes(r)) return "enduser";
  if (["super_admin", "super admin", "super-admin"].includes(r)) return "admin";
  if (r === "admin") return "admin";
  if (r === "owner") return "owner";
  if (r === "customer") return "customer";
  return r;
};

const normalizeStatus = (status) => (status || "").toString().trim().toLowerCase();

const isOwnerOrAdmin = (role) => {
  const normalized = normalizeRole(role);
  return normalized === "owner" || normalized === "admin";
};

const isActiveUser = (u) => {
  const s = u?.status;
  if (!s) return true;
  return normalizeStatus(s) === "active";
};

const getDisplayName = (u) =>
  (u?.fullName || u?.displayName || u?.email || "User").toString().trim();

const getInitials = (nameOrEmail) => {
  const s = (nameOrEmail || "").toString().trim();
  if (!s) return "U";
  const parts = s.split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const parseSeatLimit = (v) => {
  if (v === null || v === undefined) return 0;
  if (typeof v === "number" && Number.isFinite(v) && v >= 0) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }
  return 0;
};

const getPaidSeatLimitsFromCompany = (company) => ({
  admin: parseSeatLimit(company?.seatLimits?.admin),
  endUser: parseSeatLimit(company?.seatLimits?.endUser),
});

const getGiftSeatLimitsFromCompany = (company) => ({
  admin: parseSeatLimit(company?.giftSeatLimits?.admin),
  endUser: parseSeatLimit(company?.giftSeatLimits?.endUser),
});

const pickAccountPrimaryContact = (acc) => {
  const contactName =
    acc?.primaryContact?.name ||
    acc?.contactName ||
    acc?.contact ||
    "";

  const email =
    acc?.primaryContact?.email ||
    acc?.contactEmail ||
    acc?.email ||
    "";

  return {
    contactName: (contactName || "").toString().trim(),
    email: normalizeEmail(email),
  };
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const uniqStrings = (values) =>
  Array.from(
    new Set(
      safeArray(values)
        .map((value) => {
          if (typeof value === "string" || typeof value === "number") return String(value).trim();
          if (value && typeof value === "object") return String(value.id || value.uid || value.value || "").trim();
          return "";
        })
        .filter(Boolean)
    )
  );

const getAccountGroupIds = (account) =>
  uniqStrings([
    ...(safeArray(account?.groupIds)),
    ...(safeArray(account?.groups)),
  ]);

const getAccountTerritoryId = (account) =>
  String(
    account?.territoryId ||
      account?.territoryID ||
      account?.territory_id ||
      account?.territory?.id ||
      account?.territory?.territoryId ||
      ""
  ).trim();

const getUserGroupIds = (user) =>
  uniqStrings([
    ...(safeArray(user?.groupIds)),
    ...(safeArray(user?.groups)),
  ]);

const getUserTerritoryIds = (user) =>
  uniqStrings([
    ...(safeArray(user?.territoryIds)),
    ...(safeArray(user?.territories)),
    user?.territoryId,
  ]);

const isEndUserAssignedToAccount = (account, user, fbUid) => {
  const myIds = new Set(
    uniqStrings([
      user?.id,
      user?.uid,
      fbUid,
    ])
  );
  if (!myIds.size) return false;

  const assignedIds = uniqStrings([
    account?.assignedToId,
    account?.assignedUserId,
    account?.assignedTo,
    ...(safeArray(account?.assignedUserIds)),
  ]);

  return assignedIds.some((id) => myIds.has(id));
};

const canEndUserAccessAccount = (account, user, fbUid) => {
  const myUid = String(user?.id || user?.uid || fbUid || "").trim();
  if (!myUid) return false;

  const createdBy = String(account?.createdBy || account?.createdByUserId || "").trim();
  const assignedToId = String(account?.assignedToId || account?.assignedUserId || "").trim();
  const ownerUserId = String(account?.ownerUserId || account?.userId || "").trim();

  if (createdBy === myUid || assignedToId === myUid || ownerUserId === myUid) return true;

  const accountUserIds = uniqStrings([
    ...(safeArray(account?.userIds)),
    ...(safeArray(account?.visibleToUserIds)),
    ...(safeArray(account?.sharedWithUserIds)),
    ...(safeArray(account?.assignedUserIds)),
  ]);
  if (accountUserIds.includes(myUid)) return true;

  const myGroupIds = new Set(getUserGroupIds(user));
  const accountGroupIds = getAccountGroupIds(account);
  if (accountGroupIds.some((groupId) => myGroupIds.has(groupId))) return true;

  const myTerritoryIds = new Set(getUserTerritoryIds(user));
  const accountTerritoryId = getAccountTerritoryId(account);
  if (accountTerritoryId && myTerritoryIds.has(accountTerritoryId)) return true;

  return false;
};

const buildBlankInviteForm = (roleDefault = "customer") => ({
  role: roleDefault, // "customer" | "endUser" | "admin"
  contactName: "",
  email: "",
  accountName: "",
  accountId: "",

  // New-account fields (used only if accountId === "__new__")
  accountType: "Restaurant",
  accountNumber: "",
  accountPhone: "",
  addressStreet: "",
  addressStreet2: "",
  addressCity: "",
  addressState: "",
  addressZip: "",
});

const SYS_GROUP_IDS = {
  ALL_STAFF: "grp_all_staff",
  INACTIVE_STAFF: "grp_inactive_staff",
};

// ------------------------------
// Tabs
// ------------------------------
const TABS = {
  INVITES: "invites",
  GROUPS: "groups",
  TERRITORIES: "territories",
};

const TeamScreen = () => {
  const auth = useMemo(() => getAuth(), []);
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(TABS.INVITES);

  const [firebaseUser, setFirebaseUser] = useState(null);
  const [currentUserDoc, setCurrentUserDoc] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [company, setCompany] = useState(null);

  const [teamUsers, setTeamUsers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [sampleOrders, setSampleOrders] = useState([]);

  // ✅ NEW: accountId -> {name,type} map for customer display
  const [accountMap, setAccountMap] = useState({});

  // ✅ NEW: categorized lists (for display + safe filtering)
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [customerUsers, setCustomerUsers] = useState([]);

  const [customerAccount, setCustomerAccount] = useState(null);
  const [assignedRep, setAssignedRep] = useState(null);

  const [inviteForm, setInviteForm] = useState(buildBlankInviteForm("customer"));
  const [accountSelected, setAccountSelected] = useState(false);

  const [inviting, setInviting] = useState(false);
  const [busyInviteId, setBusyInviteId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Customer invite email lookup state
  const [checkingCustomerEmail, setCheckingCustomerEmail] = useState(false);
  const [existingCustomerUserByEmail, setExistingCustomerUserByEmail] = useState(null);
  const [pendingCustomerInviteByEmail, setPendingCustomerInviteByEmail] = useState(null);
  const accountsRef = useRef([]);

  const inviteUserAndSendEmail = useMemo(
    () => httpsCallable(functions, "inviteUserAndSendEmailV2"),
    []
  );

  useEffect(() => {
    accountsRef.current = Array.isArray(accounts) ? accounts : [];
  }, [accounts]);

  // -----------------------------------
  // Derived values (role)
  // -----------------------------------
  const myRole = normalizeRole(currentUserDoc?.role);
  const isAdminView = myRole === "admin" || myRole === "owner";
  const isCustomerView = myRole === "customer";
  const companyFeatureFlags = company?.featureFlags || {};
  const onlyOwnerCanDeactivateUsers = companyFeatureFlags.onlyOwnerCanDeactivateUsers !== false;
  const onlyOwnersCanAddAdmins = companyFeatureFlags.onlyOwnersCanAddAdmins !== false;
  const canInviteTeamMembers = myRole === "owner" || myRole === "admin";
  const canInviteAdmins = myRole === "owner" || (myRole === "admin" && !onlyOwnersCanAddAdmins);
  const canDeactivateStaff = myRole === "owner" || (myRole === "admin" && !onlyOwnerCanDeactivateUsers);
  const canManageRoleChanges = myRole === "owner" || (myRole === "admin" && !onlyOwnersCanAddAdmins);

  useEffect(() => {
    if (isCustomerView) return;
    if (!Object.values(TABS).includes(activeTab)) setActiveTab(TABS.INVITES);
  }, [activeTab, isCustomerView]);

  // -----------------------------------
  // Load helpers
  // -----------------------------------
  const loadTeamUsers = useCallback(async (compId, me, fbUid) => {
    if (!compId) return;

    const role = normalizeRole(me?.role);
    if (role === "customer") {
      setTeamUsers([]);
      return;
    }

    try {
      const usersCol = collection(db, "users");

      if (role === "admin" || role === "owner") {
        let companyUsersSnap;
        try {
          companyUsersSnap = await getDocs(query(usersCol, where("companyId", "==", compId), limit(1000)));
        } catch (companyUsersErr) {
          console.error("[TeamScreen] Company users query failed:", companyUsersErr);
          throw companyUsersErr;
        }

        let customerMemberships = [];
        try {
          customerMemberships = await loadCompanyCustomerMemberships(db, compId);
        } catch (customerMembershipsErr) {
          console.warn("[TeamScreen] Customer memberships query failed:", customerMembershipsErr);
          customerMemberships = [];
        }

        const listMap = new Map(
          companyUsersSnap.docs.map((userDoc) => [userDoc.id, { id: userDoc.id, ...userDoc.data() }])
        );

        const membershipByUid = new Map();
        customerMemberships.forEach((membership) => {
          const uid = membership?.uid;
          if (!uid) return;
          membershipByUid.set(uid, {
            ...membership,
            companyId: compId,
          });
        });

        membershipByUid.forEach((membership, uid) => {
          const existingUser = listMap.get(uid) || { id: uid, uid };
          const membershipAccountIds = Array.isArray(membership?.accountIds)
            ? membership.accountIds.filter(Boolean)
            : membership?.accountId
            ? [membership.accountId]
            : [];
          listMap.set(uid, {
            ...existingUser,
            id: uid,
            uid,
            role: "customer",
            companyId: compId,
            fullName:
              existingUser?.fullName ||
              membership?.fullName ||
              membership?.customerName ||
              membership?.name ||
              "Portal customer",
            email: existingUser?.email || membership?.email || "",
            accountId:
              membership?.primaryAccountId ||
              membership?.accountId ||
              existingUser?.accountId ||
              null,
            accountIds: membershipAccountIds,
            accountName: membership?.accountName || existingUser?.accountName || "",
            accountNames: [
              membership?.accountName || "",
              ...(Array.isArray(existingUser?.accountNames) ? existingUser.accountNames : []),
            ].filter(Boolean),
            repUserId: membership?.repUserId || existingUser?.repUserId || null,
            activeCustomerMembership: membership,
            customerMemberships: [membership],
          });
        });

        const list = Array.from(listMap.values());

        // Ensure "me" is present even if for some reason not returned
        const myUid = me?.id || me?.uid || fbUid || null;
        if (myUid && !list.some((u) => (u.id || u.uid) === myUid)) {
          list.push({ id: myUid, ...(me || {}) });
        }

        list.sort((a, b) =>
          getDisplayName(a)
            .toLowerCase()
            .localeCompare(getDisplayName(b).toLowerCase())
        );

        setTeamUsers(list);
        return;
      }

      const myUid = me?.id || me?.uid || fbUid || null;
      if (!myUid) {
        setTeamUsers([]);
        return;
      }

      setTeamUsers([me]);
    } catch (err) {
      console.error("[TeamScreen] Error loading team users:", err);
      setTeamUsers([]);
      if ((role === "admin" || role === "owner") && err?.code === "permission-denied") {
        setError("You don't have permission to view team members.");
      }
    }
  }, []);

  const loadPendingInvites = useCallback(async (compId, me, fbUid) => {
    if (!compId) return;

    const role = normalizeRole(me?.role);
    if (role === "customer") {
      setPendingInvites([]);
      return;
    }

    try {
      const invitesCol = collection(db, "invites");

      if (role === "admin" || role === "owner") {
        const snap = await getDocs(
          query(
            invitesCol,
            where("companyId", "==", compId),
            where("status", "==", "Pending")
          )
        );

        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const aSec = a.createdAt?.seconds || 0;
          const bSec = b.createdAt?.seconds || 0;
          return bSec - aSec;
        });

        setPendingInvites(list);
        return;
      }

      const myUid = me?.id || me?.uid || fbUid || null;
      if (!myUid) {
        setPendingInvites([]);
        return;
      }

      const pendingMap = new Map();
      const collect = (snap) => {
        snap.docs.forEach((d) => {
          pendingMap.set(d.id, { id: d.id, ...d.data() });
        });
      };

      const ownSnap = await getDocs(
        query(
          invitesCol,
          where("companyId", "==", compId),
          where("status", "==", "Pending"),
          where("role", "==", "customer"),
          where("createdBy", "==", myUid)
        )
      );
      collect(ownSnap);

      const visibleAccountIds = Array.from(
        new Set(
          (accountsRef.current || [])
            .map((account) => String(account?.id || "").trim())
            .filter(Boolean)
        )
      );
      const chunkIds = (arr, size = 10) => {
        const chunks = [];
        for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
        return chunks;
      };

      for (const ids of chunkIds(visibleAccountIds)) {
        if (!ids.length) continue;
        const accountSnap = await getDocs(
          query(
            invitesCol,
            where("companyId", "==", compId),
            where("status", "==", "Pending"),
            where("role", "==", "customer"),
            where("accountId", "in", ids)
          )
        );
        collect(accountSnap);
      }

      const list = Array.from(pendingMap.values());
      list.sort((a, b) => {
        const aSec = a.createdAt?.seconds || 0;
        const bSec = b.createdAt?.seconds || 0;
        return bSec - aSec;
      });

      setPendingInvites(list);
    } catch (err) {
      console.error("[TeamScreen] Error loading pending invites:", err);
      setPendingInvites([]);
      if (err?.code === "permission-denied") {
        setError("You don't have permission to view pending invites.");
      }
    }
  }, []);

  const loadAccounts = useCallback(async (compId, me, fbUid) => {
    if (!compId) return;

    try {
      const accCol = collection(db, "companies", compId, "accounts");
      const snap = await getDocs(accCol);

      const role = normalizeRole(me?.role);
      const baseList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const list = (role === "admin" || role === "owner"
        ? baseList
        : role === "enduser"
        ? baseList.filter((account) => isEndUserAssignedToAccount(account, me, fbUid))
        : baseList.filter((account) => canEndUserAccessAccount(account, me, fbUid)))
        .sort((a, b) =>
          (a.name || a.accountName || "").localeCompare(b.name || b.accountName || "")
        );

      setAccounts(list);
    } catch (err) {
      console.error("[TeamScreen] Error loading accounts:", err);
      setAccounts([]);
      if (err?.code === "permission-denied") {
        setError("You don't have permission to view accounts.");
      }
    }
  }, []);

  const loadSampleOrders = useCallback(async (compId, me) => {
    if (!compId) return;
    if (!isOwnerOrAdmin(me?.role)) {
      setSampleOrders([]);
      return;
    }

    try {
      const ordersCol = collection(db, "companies", compId, "sampleOrders");
      const snap = await getDocs(ordersCol);
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSampleOrders(list);
    } catch (err) {
      if (err?.code === "permission-denied") {
        console.warn("[TeamScreen] Sample orders unavailable for this view; continuing without them.");
      } else {
        console.error("[TeamScreen] Error loading sample orders:", err);
      }
      setSampleOrders([]);
    }
  }, []);

  const loadCustomerAssignment = useCallback(async (compId, me) => {
    setCustomerAccount(null);
    setAssignedRep(null);

    if (!compId) return;
    const role = normalizeRole(me?.role);
    if (role !== "customer") return;

    try {
      const myAccountId =
        me?.activeCustomerMembership?.primaryAccountId ||
        me?.activeCustomerMembership?.accountId ||
        me?.accountId ||
        (Array.isArray(me?.customerAccountIds) ? me.customerAccountIds[0] : null) ||
        null;
      if (!myAccountId) return;

      const accSnap = await getDoc(doc(db, "companies", compId, "accounts", myAccountId));
      if (!accSnap.exists()) return;

      const acc = { id: accSnap.id, ...accSnap.data() };
      setCustomerAccount(acc);

      const repUid = acc.assignedToId || acc.assignedUserId || null;
      if (!repUid) return;

      const repSnap = await getDoc(doc(db, "users", repUid));
      if (!repSnap.exists()) return;

      setAssignedRep({ id: repSnap.id, ...repSnap.data() });
    } catch (err) {
      console.error("[TeamScreen] Error loading customer assignment:", err);
      if (err?.code === "permission-denied") {
        setError("You don't have permission to view your assigned rep.");
      }
    }
  }, []);

  // -----------------------------------
  // ✅ NEW: accounts -> accountMap
  // -----------------------------------
  useEffect(() => {
    if (!accounts || !Array.isArray(accounts)) {
      setAccountMap({});
      return;
    }

    const map = {};
    accounts.forEach((acc) => {
      if (acc?.id) {
        map[acc.id] = {
          name: acc.name || acc.accountName || "",
          type: acc.accountType || "",
        };
      }
    });
    setAccountMap(map);
  }, [accounts]);

  // -----------------------------------
  // ✅ NEW: enrich teamUsers with customer accountName (from accountMap)
  // -----------------------------------
  const teamUsersEnriched = useMemo(() => {
    if (!teamUsers || !Array.isArray(teamUsers)) return [];

    return teamUsers.map((u) => {
      const role = normalizeRole(u?.role);
      if (role !== "customer") return u;

      const accountIds = Array.from(
        new Set(
          []
            .concat(Array.isArray(u?.accountIds) ? u.accountIds : [])
            .concat(u?.accountId || [])
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        )
      );
      const primaryAccountId = String(u?.accountId || accountIds[0] || "").trim();
      const primaryAccount = primaryAccountId ? accountMap?.[primaryAccountId] : null;
      const accountNames = Array.from(
        new Set(
          accountIds
            .map((accountId) => accountMap?.[accountId]?.name || "")
            .concat(Array.isArray(u?.accountNames) ? u.accountNames : [])
            .concat(u?.accountName || [])
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        )
      );

      return {
        ...u,
        accountId: primaryAccountId || "",
        accountIds,
        accountName: (u?.accountName || primaryAccount?.name || accountNames[0] || "").toString(),
        accountNames,
        accountType: (u?.accountType || primaryAccount?.type || "").toString(),
      };
    });
  }, [teamUsers, accountMap]);

  // -----------------------------------
  // ✅ NEW: categorize users (Active / Inactive / Customers)
  // -----------------------------------
  useEffect(() => {
    if (!teamUsersEnriched || !Array.isArray(teamUsersEnriched)) {
      setActiveUsers([]);
      setInactiveUsers([]);
      setCustomerUsers([]);
      return;
    }

    const active = [];
    const inactive = [];
    const customers = [];

    teamUsersEnriched.forEach((user) => {
      const role = normalizeRole(user?.role);
      const status = normalizeStatus(user?.status || "active");

      if (role === "customer") {
        customers.push(user);
      } else if (status === "active") {
        active.push(user);
      } else {
        inactive.push(user);
      }
    });

    active.sort((a, b) =>
      getDisplayName(a).toLowerCase().localeCompare(getDisplayName(b).toLowerCase())
    );
    inactive.sort((a, b) =>
      getDisplayName(a).toLowerCase().localeCompare(getDisplayName(b).toLowerCase())
    );
    customers.sort((a, b) =>
      getDisplayName(a).toLowerCase().localeCompare(getDisplayName(b).toLowerCase())
    );

    setActiveUsers(active);
    setInactiveUsers(inactive);
    setCustomerUsers(customers);
  }, [teamUsersEnriched]);

  const staffUsersOnly = useMemo(() => {
    return (teamUsersEnriched || []).filter((u) => normalizeRole(u?.role) !== "customer");
  }, [teamUsersEnriched]);

  const inviteScopedLists = useMemo(() => {
    if (isAdminView) {
      return {
        activeUsers,
        inactiveUsers,
        customerUsers,
      };
    }

    const myUid = firebaseUser?.uid || currentUserDoc?.id || currentUserDoc?.uid || null;
    if (!myUid) {
      return {
        activeUsers: [],
        inactiveUsers: [],
        customerUsers: [],
      };
    }

    const visibleAccountIds = new Set((accounts || []).map((account) => account?.id).filter(Boolean));

    const visibleCustomers = (customerUsers || []).filter((user) => {
      const repUserId = (user?.repUserId || "").toString().trim();
      const createdBy = (user?.createdBy || "").toString().trim();
      const accountIds = []
        .concat(Array.isArray(user?.accountIds) ? user.accountIds : [])
        .concat(user?.accountId || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      const hasVisibleAccount = accountIds.some((accountId) => visibleAccountIds.has(accountId));
      return repUserId === myUid || createdBy === myUid || hasVisibleAccount;
    });

    const customerMap = new Map();
    visibleCustomers.forEach((customer) => {
      const key =
        (customer?.id || customer?.uid || `${customer?.accountId || ""}:${customer?.email || ""}`).toString().trim();
      if (!key) return;
      customerMap.set(key, customer);
    });

    return {
      activeUsers: [],
      inactiveUsers: [],
      customerUsers: Array.from(customerMap.values()),
    };
  }, [accounts, activeUsers, inactiveUsers, customerUsers, currentUserDoc, firebaseUser, isAdminView]);

  // -----------------------------------
  // Bootstrap user + company + team
  // -----------------------------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      setLoading(true);
      setError("");

      if (!fbUser) {
        setFirebaseUser(null);
        setCurrentUserDoc(null);
        setCompanyId(null);
        setCompany(null);
        setTeamUsers([]);
        setPendingInvites([]);
        setAccounts([]);
        setAccountMap({});
        setActiveUsers([]);
        setInactiveUsers([]);
        setCustomerUsers([]);
        setCustomerAccount(null);
        setAssignedRep(null);
        setExistingCustomerUserByEmail(null);
        setPendingCustomerInviteByEmail(null);
        setCheckingCustomerEmail(false);
        setInviteForm(buildBlankInviteForm("customer"));
        setAccountSelected(false);
        setLoading(false);
        return;
      }

      setFirebaseUser(fbUser);

      try {
        const userRef = doc(db, "users", fbUser.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          setError("User profile not found. Please contact support.");
          setCurrentUserDoc(null);
          setLoading(false);
          return;
        }

        const userData = { id: userSnap.id, ...userSnap.data() };
        setCurrentUserDoc(userData);

        const compId = (userData.companyId || "").toString().trim();
        if (!compId) {
          setError(
            "Your user profile is missing companyId. Run the migration script to backfill it."
          );
          setCompanyId(null);
          setCompany(null);
          setTeamUsers([]);
          setPendingInvites([]);
          setAccounts([]);
          setSampleOrders([]);
          setLoading(false);
          return;
        }

        setCompanyId(compId);

        const companyRef = doc(db, "companies", compId);
        const companySnap = await getDoc(companyRef);
        setCompany(companySnap.exists() ? { id: companySnap.id, ...companySnap.data() } : null);

        await loadAccounts(compId, userData, fbUser.uid);

        const r = normalizeRole(userData.role);

        const params = new URLSearchParams(window.location.search);
        const tabParam = (params.get("tab") || "").toLowerCase().trim();
        const mappedTab =
          tabParam === "groups"
            ? TABS.GROUPS
            : tabParam === "territories"
            ? TABS.TERRITORIES
            : TABS.INVITES;

        if (r === "customer") {
          await loadCustomerAssignment(compId, userData);
          setInviteForm(buildBlankInviteForm("customer"));
          setAccountSelected(false);
        } else {
          await Promise.all([
            loadTeamUsers(compId, userData, fbUser.uid),
            loadPendingInvites(compId, userData, fbUser.uid),
            loadSampleOrders(compId, userData),
          ]);

          setInviteForm(buildBlankInviteForm("customer"));
          setAccountSelected(false);
          setActiveTab(mappedTab);
        }
      } catch (err) {
        console.error("[TeamScreen] Error loading team screen data:", err);
        setError("Failed to load team data. Please try again.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [auth, loadAccounts, loadCustomerAssignment, loadPendingInvites, loadSampleOrders, loadTeamUsers]);

  // -----------------------------------
  // Seat summary (ACTIVE staff only)
  // -----------------------------------
  const seatSummary = useMemo(() => {
    const activeStaff = (Array.isArray(staffUsersOnly) ? staffUsersOnly : []).filter((u) =>
      isActiveUser(u)
    );

    const staff = activeStaff.filter((u) => {
      const r = normalizeRole(u?.role);
      return r === "owner" || r === "admin" || r === "enduser";
    });

    const usedAdmins = staff.filter((u) => {
      const r = normalizeRole(u?.role);
      return r === "admin" || r === "owner";
    }).length;

    const usedEndUsers = staff.filter((u) => normalizeRole(u?.role) === "enduser").length;

    const paid = getPaidSeatLimitsFromCompany(company);
    const gift = getGiftSeatLimitsFromCompany(company);

    const adminEffective = paid.admin + gift.admin;
    const endUserEffective = paid.endUser + gift.endUser;

    const adminLeft = Math.max(adminEffective - usedAdmins, 0);
    const endUserLeft = Math.max(endUserEffective - usedEndUsers, 0);

    return {
      admin: {
        used: usedAdmins,
        paidLimit: paid.admin,
        gift: gift.admin,
        effectiveLimit: adminEffective,
        left: adminLeft,
      },
      endUser: {
        used: usedEndUsers,
        paidLimit: paid.endUser,
        gift: gift.endUser,
        effectiveLimit: endUserEffective,
        left: endUserLeft,
      },
    };
  }, [company, staffUsersOnly]);

  const seatsForInvites = useMemo(() => {
    return {
      adminSeatsLeft: seatSummary.admin.left,
      adminSeatsUsed: seatSummary.admin.used,
      adminSeatsLimit: seatSummary.admin.effectiveLimit,

      endUserSeatsLeft: seatSummary.endUser.left,
      endUserSeatsUsed: seatSummary.endUser.used,
      endUserSeatsLimit: seatSummary.endUser.effectiveLimit,

      adminSeatsPaidLimit: seatSummary.admin.paidLimit,
      adminSeatsGift: seatSummary.admin.gift,
      endUserSeatsPaidLimit: seatSummary.endUser.paidLimit,
      endUserSeatsGift: seatSummary.endUser.gift,
    };
  }, [seatSummary]);

  const SeatPill = ({ label, used, effectiveLimit, left, gift }) => {
    const isLow = left <= 0;

    return (
      <div
        className={`inline-flex items-center gap-2 rounded-2xl px-3 py-2 text-[11px] font-semibold shadow-sm backdrop-blur-sm ${
          isLow
            ? "bg-rose-50/95 text-rose-800 ring-1 ring-rose-200"
            : "bg-white/90 text-slate-700 ring-1 ring-slate-200"
        }`}
        title={`${label}: ${used}/${effectiveLimit} used (${left} left)${
          gift > 0 ? ` • gift +${gift}` : ""
        }`}
      >
        <span className="whitespace-nowrap">{label}</span>
        <span className="text-slate-400">•</span>
        <span className="whitespace-nowrap">
          {used}/{effectiveLimit} used
        </span>
        <span className="text-slate-400">•</span>
        <span className="whitespace-nowrap">{left} left</span>

        {gift > 0 ? (
          <>
            <span className="text-slate-400">•</span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap">
              <Gift className="w-3.5 h-3.5" />
              +{gift}
            </span>
          </>
        ) : null}
      </div>
    );
  };

  // -----------------------------------
  // Invite helpers
  // -----------------------------------
  const getAppBaseUrl = useCallback(() => {
    const envBase = (process.env.REACT_APP_APP_BASE_URL || "").toString().trim();
    const base = envBase || `${window.location.origin}/app`;
    return base.replace(/\/$/, "");
  }, []);

  const refreshAll = useCallback(async () => {
    if (!companyId) return;
    await Promise.all([
      loadTeamUsers(companyId, currentUserDoc, firebaseUser?.uid),
      loadPendingInvites(companyId, currentUserDoc, firebaseUser?.uid),
      loadAccounts(companyId, currentUserDoc, firebaseUser?.uid),
      loadSampleOrders(companyId, currentUserDoc),
    ]);
  }, [companyId, currentUserDoc, firebaseUser?.uid, loadAccounts, loadPendingInvites, loadSampleOrders, loadTeamUsers]);

  const userAssignmentStats = useMemo(() => {
    const accountCounts = {};
    (Array.isArray(accounts) ? accounts : []).forEach((account) => {
      const assignedId = account?.assignedToId || account?.assignedUserId || null;
      if (!assignedId) return;
      accountCounts[assignedId] = (accountCounts[assignedId] || 0) + 1;
    });

    const sampleOrderCounts = {};
    (Array.isArray(sampleOrders) ? sampleOrders : []).forEach((order) => {
      const assignedId = order?.assignedToId || order?.assignedUserId || null;
      if (!assignedId) return;
      sampleOrderCounts[assignedId] = (sampleOrderCounts[assignedId] || 0) + 1;
    });

    const merged = {};
    const keys = new Set([...Object.keys(accountCounts), ...Object.keys(sampleOrderCounts)]);
    keys.forEach((id) => {
      merged[id] = {
        accountsAssigned: accountCounts[id] || 0,
        sampleOrdersAssigned: sampleOrderCounts[id] || 0,
      };
    });
    return merged;
  }, [accounts, sampleOrders]);

  // ✅ account select (existing)
  const handleAccountSelect = useCallback(
    (accountIdSelected) => {
      if (!accountIdSelected) {
        setAccountSelected(false);
        setInviteForm((prev) => ({
          ...prev,
          accountId: "",
          accountName: "",
          contactName: "",
          email: "",
        }));
        return;
      }

      const selectedAccount = accounts.find((acc) => acc.id === accountIdSelected);
      if (!selectedAccount) {
        setAccountSelected(false);
        setInviteForm((prev) => ({
          ...prev,
          role: "customer",
          accountId: accountIdSelected,
        }));
        return;
      }

      const accountName = selectedAccount.name || selectedAccount.accountName || "";
      const { contactName, email } = pickAccountPrimaryContact(selectedAccount);

      setAccountSelected(true);
      setInviteForm((prev) => ({
        ...prev,
        role: "customer",
        accountId: accountIdSelected,
        accountName,
        contactName: contactName || "",
        email: email || "",
      }));
    },
    [accounts]
  );

  const handleOpenSelectedAccount = useCallback(
    (accountIdSelected) => {
      const accountId = String(accountIdSelected || "").trim();
      if (!accountId) return;
      const basePrefix = window.location.pathname.startsWith("/app") ? "/app" : "";
      navigate(`${basePrefix}/accounts?edit=${encodeURIComponent(accountId)}`);
    },
    [navigate]
  );

  // ✅ Deep-link support: /app/team?tab=invites&accountId=XYZ
  const didApplyUrlAccountRef = useRef(false);
  useEffect(() => {
    if (didApplyUrlAccountRef.current) return;
    if (!accounts || accounts.length === 0) return;
    if (isCustomerView) return;

    const params = new URLSearchParams(window.location.search);
    const accountIdParam = (params.get("accountId") || "").trim();
    if (!accountIdParam) return;

    didApplyUrlAccountRef.current = true;
    setActiveTab(TABS.INVITES);
    handleAccountSelect(accountIdParam);
  }, [accounts, handleAccountSelect, isCustomerView]);

  // ✅ customer email lookup (company-scoped)
  useEffect(() => {
    const run = async () => {
      const roleNorm = normalizeRole(inviteForm.role);
      const email = normalizeEmail(inviteForm.email);

      if (!companyId || roleNorm !== "customer" || !email || !isValidEmail(email)) {
        setExistingCustomerUserByEmail(null);
        setPendingCustomerInviteByEmail(null);
        setCheckingCustomerEmail(false);
        return;
      }

      setCheckingCustomerEmail(true);

      try {
        // Existing customer user
        let foundUser = null;
        try {
          const usersSnap = await getDocs(
            query(collection(db, "users"), where("email", "==", email), limit(10))
          );
          const candidates = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

          foundUser =
            candidates.find((u) => {
              if ((u.companyId || "") !== companyId) return false;
              if (normalizeRole(u.role) !== "customer") return false;
              return true;
            }) || null;
        } catch (err) {
          console.warn("[TeamScreen] email->user lookup failed:", err);
        }

        setExistingCustomerUserByEmail(foundUser);

        if (foundUser) {
          setPendingCustomerInviteByEmail(null);
          return;
        }

        // Pending invite
        let foundInvite = null;
        try {
          const invSnap = await getDocs(
            query(collection(db, "invites"), where("email", "==", email), limit(20))
          );
          const invs = invSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

          foundInvite =
            invs.find((inv) => {
              if ((inv.companyId || "") !== companyId) return false;
              if (normalizeRole(inv.role) !== "customer") return false;
              return normalizeStatus(inv.status) === "pending";
            }) || null;
        } catch (err) {
          console.warn("[TeamScreen] email->invite lookup failed:", err);
        }

        setPendingCustomerInviteByEmail(foundInvite);
      } finally {
        setCheckingCustomerEmail(false);
      }
    };

    run();
  }, [companyId, inviteForm.role, inviteForm.email]);

  // -----------------------------------
  // Invite actions (UNCHANGED LOGIC, strict companyId)
  // -----------------------------------
  const handleInviteUser = useCallback(
    async (e) => {
      e.preventDefault();
      if (!firebaseUser || !companyId) return;

      setError("");

      const requestedRoleUi = (inviteForm.role || "customer").toString().trim();
      const roleToInviteNorm = isAdminView ? normalizeRole(requestedRoleUi) : "customer";

      if (!["admin", "enduser", "customer"].includes(roleToInviteNorm)) {
        return setError("Invalid role selected.");
      }
      if (roleToInviteNorm === "enduser" && !canInviteTeamMembers) {
        return setError("You do not have permission to invite team members.");
      }
      if (roleToInviteNorm === "admin" && !canInviteAdmins) {
        return setError("Only the owner can invite admins right now.");
      }

      if (roleToInviteNorm === "admin" && (seatsForInvites.adminSeatsLeft ?? 0) <= 0) {
        return setError("No admin seats left.");
      }
      if (roleToInviteNorm === "enduser" && (seatsForInvites.endUserSeatsLeft ?? 0) <= 0) {
        return setError("No team seats left.");
      }

      const email = normalizeEmail(inviteForm.email);
      const contactName = (inviteForm.contactName || "").toString().trim();
      const companyName = company?.businessName || company?.name || "SupplyHawk";

      if (!contactName) return setError("Contact name is required to send an invite.");
      if (!email) return setError("Email is required to send an invite.");
      if (!isValidEmail(email)) return setError("Please enter a valid email address.");

      const isCustomerInvite = roleToInviteNorm === "customer";

      if (isCustomerInvite && existingCustomerUserByEmail) {
        return setError("That email already has portal access (customer user exists).");
      }
      if (isCustomerInvite && pendingCustomerInviteByEmail) {
        return setError("That email already has a pending invite.");
      }

      let accountId = isCustomerInvite ? (inviteForm.accountId || "").toString().trim() : "";
      let accountName = isCustomerInvite ? (inviteForm.accountName || "").toString().trim() : "";

      setInviting(true);

      try {
        if (isCustomerInvite && accountId === "__new__") {
          if (!accountName) {
            setInviting(false);
            return setError("Account Name is required for a new account.");
          }

          const staffGroupId = company?.staffGroupId || null;
          const groupIds = staffGroupId ? [staffGroupId] : [];

          const acctNumber =
            (inviteForm.accountNumber || "").toString().trim() ||
            `ACCT-${Math.floor(1000 + Math.random() * 9000)}`;

          const street1 = (inviteForm.addressStreet || "").toString().trim();
          const street2 = (inviteForm.addressStreet2 || "").toString().trim() || null;
          const city = (inviteForm.addressCity || "").toString().trim();
          const state = (inviteForm.addressState || "").toString().trim();
          const zip = (inviteForm.addressZip || "").toString().trim();
          const phone = (inviteForm.accountPhone || "").toString().trim() || null;

          const accountPayload = {
            accountNumber: acctNumber,
            active: true,
            name: accountName,

            phone,
            contact: contactName,
            contactName,
            email,
            contactEmail: email,
            primaryContact: { name: contactName, email, phone: null, title: null },

            accountType: (inviteForm.accountType || "Restaurant").toString().trim(),

            address: {
              line1: street1 || null,
              line2: street2,
              city: city || null,
              state: state || null,
              zip: zip || null,
              country: "USA",
            },

            addressStreet: street1 || null,
            addressStreet2: street2,
            addressCity: city || null,
            addressState: state || null,
            addressZip: zip || null,

            city: city || null,
            state: state || null,
            zip: zip || null,

            groupIds,

            flaggedAs: "None",
            stage: "Prospect",
            visited: false,
            hasSampleOrder: false,

            createdAt: serverTimestamp(),
            createdBy: firebaseUser.uid,
            updatedAt: serverTimestamp(),
            updatedBy: firebaseUser.uid,
          };

          const created = await addDoc(collection(db, "companies", companyId, "accounts"), accountPayload);

          accountId = created.id;
          setAccountSelected(true);
          setInviteForm((prev) => ({ ...prev, accountId: created.id }));
        }

        if (isCustomerInvite && !accountId) {
          setInviting(false);
          return setError("Please select an account (or choose New account).");
        }

        const payload = {
          companyId,
          companyName,
          role: roleToInviteNorm === "enduser" ? "endUser" : roleToInviteNorm,
          email,
          contactName,
          name: contactName,
          accountId: isCustomerInvite ? accountId : "",
          accountName: isCustomerInvite ? accountName : "",
          businessName: isCustomerInvite ? accountName : "",
          appBaseUrl: getAppBaseUrl(),
        };

        await inviteUserAndSendEmail(payload);

        await refreshAll();

        setInviteForm(buildBlankInviteForm("customer"));
        setAccountSelected(false);
        setActiveTab(TABS.INVITES);
      } catch (err) {
        console.error("[TeamScreen] Error sending invite:", err);
        setError(err?.message || "Error sending invite.");
      } finally {
        setInviting(false);
      }
    },
    [
      company,
      companyId,
      firebaseUser,
      getAppBaseUrl,
      inviteForm,
      inviteUserAndSendEmail,
      isAdminView,
      refreshAll,
      seatsForInvites,
      existingCustomerUserByEmail,
      pendingCustomerInviteByEmail,
      canInviteTeamMembers,
      canInviteAdmins,
    ]
  );

  const handleCancelInvite = useCallback(
    async (inviteId) => {
      if (!inviteId) return;
      setBusyInviteId(inviteId);
      setError("");

      try {
        await updateDoc(doc(db, "invites", inviteId), {
          status: "Canceled",
          updatedAt: serverTimestamp(),
        });
        await loadPendingInvites(companyId, currentUserDoc, firebaseUser?.uid);
      } catch (err) {
        console.error("[TeamScreen] Error canceling invite:", err);
        setError("Failed to cancel invite.");
      } finally {
        setBusyInviteId(null);
      }
    },
    [companyId, currentUserDoc, firebaseUser?.uid, loadPendingInvites]
  );

  const handleResendInvite = useCallback(
    async (invite) => {
      if (!invite?.id) return;
      const inviteRole = normalizeRole(invite?.role);
      if (inviteRole === "enduser" && !canInviteTeamMembers) {
        setError("You do not have permission to resend team invites.");
        return;
      }
      if (inviteRole === "admin" && !canInviteAdmins) {
        setError("Only the owner can resend admin invites.");
        return;
      }
      setBusyInviteId(invite.id);
      setError("");

      try {
        const companyName = company?.businessName || company?.name || "SupplyHawk";
        const roleNorm = normalizeRole(invite.role || "endUser");
        const email = normalizeEmail(invite.email);

        const contactName = (invite.fullName || invite.name || "Team member").toString().trim();
        const accountName = (invite.accountName || "").toString().trim();

        const payload = {
          companyId,
          companyName,
          role: roleNorm === "enduser" ? "endUser" : roleNorm,
          email,
          contactName,
          name: contactName,
          accountId: roleNorm === "customer" ? invite.accountId || "" : "",
          accountName: roleNorm === "customer" ? accountName : "",
          businessName: roleNorm === "customer" ? accountName : "",
          appBaseUrl: getAppBaseUrl(),
          existingInviteId: invite.id,
        };

        await inviteUserAndSendEmail(payload);

        await loadPendingInvites(companyId, currentUserDoc, firebaseUser?.uid);
      } catch (err) {
        console.error("[TeamScreen] Error resending invite email:", err);
        setError(err?.message || "Failed to resend invite email.");
      } finally {
        setBusyInviteId(null);
      }
    },
    [
      company,
      companyId,
      currentUserDoc,
      firebaseUser?.uid,
      getAppBaseUrl,
      inviteUserAndSendEmail,
      loadPendingInvites,
      canInviteTeamMembers,
      canInviteAdmins,
    ]
  );

  const clearAssignmentsForUser = useCallback(
    async (targetUserId) => {
      const normalizedUserId = String(targetUserId || "").trim();
      if (!companyId || !normalizedUserId) return;

      const dedupeDocs = async (queriesToRun = []) => {
        const results = await Promise.all(queriesToRun.map((q) => getDocs(q)));
        const seen = new Map();
        results.forEach((snap) => {
          snap.docs.forEach((docSnap) => {
            if (!seen.has(docSnap.id)) {
              seen.set(docSnap.id, docSnap.ref);
            }
          });
        });
        return Array.from(seen.values());
      };

      const accountsCol = collection(db, "companies", companyId, "accounts");
      const sampleOrdersCol = collection(db, "companies", companyId, "sampleOrders");

      const [accountRefs, sampleOrderRefs] = await Promise.all([
        dedupeDocs([
          query(accountsCol, where("assignedToId", "==", normalizedUserId)),
          query(accountsCol, where("assignedUserId", "==", normalizedUserId)),
          query(accountsCol, where("repUserId", "==", normalizedUserId)),
        ]),
        dedupeDocs([
          query(sampleOrdersCol, where("assignedToId", "==", normalizedUserId)),
          query(sampleOrdersCol, where("assignedUserId", "==", normalizedUserId)),
        ]),
      ]);

      await Promise.all([
        ...accountRefs.map((ref) =>
          updateDoc(ref, {
            assignedToId: null,
            assignedUserId: null,
            repUserId: null,
            assignedToName: null,
            updatedAt: serverTimestamp(),
          })
        ),
        ...sampleOrderRefs.map((ref) =>
          updateDoc(ref, {
            assignedToId: null,
            assignedUserId: null,
            assignedTo: null,
            assignedToName: null,
            updatedAt: serverTimestamp(),
          })
        ),
      ]);
    },
    [companyId]
  );

  const handleSetStaffStatus = useCallback(
    async (user, nextStatus) => {
      if (!firebaseUser || !companyId || !user?.id) return;
      if (!canDeactivateStaff) {
        setError("Only the owner can deactivate or reactivate users right now.");
        return;
      }

      setError("");

      try {
        await setTeamUserStatus(firebaseUser, user.id, nextStatus);

        const existingGroupIds = Array.isArray(user?.groupIds) ? user.groupIds.filter(Boolean) : [];
        let nextGroupIds = existingGroupIds.filter((gid) => gid !== SYS_GROUP_IDS.INACTIVE_STAFF);

        if (!nextGroupIds.includes(SYS_GROUP_IDS.ALL_STAFF)) {
          nextGroupIds = [...nextGroupIds, SYS_GROUP_IDS.ALL_STAFF];
        }

        if (normalizeStatus(nextStatus) === "inactive") {
          nextGroupIds = [...nextGroupIds, SYS_GROUP_IDS.INACTIVE_STAFF];
        }

        await updateDoc(doc(db, "users", user.id), {
          status: nextStatus,
          groupIds: Array.from(new Set(nextGroupIds)),
          updatedAt: serverTimestamp(),
        });

        try {
          await logAuditEvent(firebaseUser, {
            action:
              normalizeStatus(nextStatus) === "inactive"
                ? "team_user_deactivated"
                : "team_user_reactivated",
            entityType: "user",
            entityId: user.id,
            before: {
              status: user?.status || "Active",
              role: user?.role || "",
              groupIds: Array.isArray(user?.groupIds) ? user.groupIds : [],
            },
            after: {
              status: nextStatus,
              role: user?.role || "",
              groupIds: Array.from(new Set(nextGroupIds)),
            },
            metadata: {
              targetUserEmail: user?.email || "",
              targetUserName: user?.fullName || user?.displayName || user?.email || "User",
              assignmentsCleared: normalizeStatus(nextStatus) === "inactive",
            },
            severity: normalizeStatus(nextStatus) === "inactive" ? "warning" : "info",
          });
        } catch (auditErr) {
          console.warn("[TeamScreen] Failed to audit staff status change:", auditErr);
        }

        if (normalizeStatus(nextStatus) === "inactive") {
          await clearAssignmentsForUser(user.id);
        }

        await Promise.all([
          loadTeamUsers(companyId, currentUserDoc, firebaseUser?.uid),
          loadAccounts(companyId, currentUserDoc, firebaseUser?.uid),
          loadSampleOrders(companyId, currentUserDoc),
        ]);
      } catch (err) {
        console.error("[TeamScreen] set staff status error:", err);
        setError(err?.message || "Failed to update this team member.");
      }
    },
    [
      canDeactivateStaff,
      clearAssignmentsForUser,
      companyId,
      currentUserDoc,
      firebaseUser,
      loadAccounts,
      loadSampleOrders,
      loadTeamUsers,
    ]
  );

  const handleChangeStaffRole = useCallback(
    async (user, targetRole) => {
      if (!firebaseUser || !companyId || !user?.id) return;
      if (!canManageRoleChanges) {
        throw new Error("Only the owner can change admin or team roles right now.");
      }

      const currentRole = normalizeRole(user?.role);
      const nextRole = normalizeRole(targetRole);
      if (!["admin", "enduser"].includes(currentRole) || !["admin", "enduser"].includes(nextRole)) return;
      if (currentRole === nextRole) return;

      if (nextRole === "admin" && (seatsForInvites.adminSeatsLeft ?? 0) <= 0) {
        throw new Error("No admin seats left.");
      }
      if (nextRole === "enduser" && (seatsForInvites.endUserSeatsLeft ?? 0) <= 0) {
        throw new Error("No team seats left.");
      }

      setError("");

      const normalizedRoleValue = nextRole === "enduser" ? "endUser" : "admin";

      await updateDoc(doc(db, "users", user.id), {
        role: normalizedRoleValue,
        updatedAt: serverTimestamp(),
        updatedBy: firebaseUser.uid,
      });

      try {
        await logAuditEvent(firebaseUser, {
          action: "team_user_role_changed",
          entityType: "user",
          entityId: user.id,
          before: {
            role: user?.role || "",
            status: user?.status || "Active",
          },
          after: {
            role: normalizedRoleValue,
            status: user?.status || "Active",
          },
          metadata: {
            targetUserEmail: user?.email || "",
            targetUserName: user?.fullName || user?.displayName || user?.email || "User",
            seatsSnapshot: {
              adminLeft: seatsForInvites.adminSeatsLeft ?? 0,
              endUserLeft: seatsForInvites.endUserSeatsLeft ?? 0,
            },
          },
          severity: "warning",
        });
      } catch (auditErr) {
        console.warn("[TeamScreen] Failed to audit role change:", auditErr);
      }

      setTeamUsers((prev) =>
        (Array.isArray(prev) ? prev : []).map((entry) =>
          (entry?.id || entry?.uid) === user.id ? { ...entry, role: normalizedRoleValue } : entry
        )
      );

      await loadTeamUsers(companyId, currentUserDoc, firebaseUser?.uid);
    },
    [
      companyId,
      currentUserDoc,
      firebaseUser,
      loadTeamUsers,
      seatsForInvites.adminSeatsLeft,
      seatsForInvites.endUserSeatsLeft,
      canManageRoleChanges,
    ]
  );

  // -----------------------------------
  // Render
  // -----------------------------------
  const companyName = company?.businessName || company?.name || "Your Company";
  const hasRenderableSession = !!firebaseUser && !!currentUserDoc;

  if (loading && !hasRenderableSession) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4 -mx-4 md:-mx-6 lg:-mx-8 -mt-4 md:-mt-6 lg:-mt-8">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600 text-lg font-medium">Loading team…</p>
        </div>
      </div>
    );
  }

  if (!firebaseUser || !currentUserDoc) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-4 -mx-4 md:-mx-6 lg:-mx-8 -mt-4 md:-mt-6 lg:-mt-8">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center border border-red-100">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Not signed in</h2>
          <p className="text-slate-600">You must be logged in to view the team.</p>
        </div>
      </div>
    );
  }

  // CUSTOMER VIEW (unchanged)
  if (isCustomerView) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8 px-4 -mx-4 md:-mx-6 lg:-mx-8 -mt-4 md:-mt-6 lg:-mt-8">
        <div className="w-full max-w-none mx-0 px-0 py-1">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:items-between gap-3">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 leading-tight">Team</h1>
              <p className="text-sm text-slate-600 mt-1">Your assigned representative.</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
            <div className="p-6 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800 flex-1">{error}</p>
                </div>
              )}

              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <h2 className="text-base font-semibold text-slate-900">Your Representative</h2>
                <p className="text-xs text-slate-500 mt-1">
                  Customers do not see internal groups or territories.
                </p>

                {customerAccount && (
                  <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                    <span className="font-semibold text-slate-900">Account:</span>{" "}
                    {customerAccount.name ||
                      customerAccount.accountName ||
                      customerAccount.companyName ||
                      customerAccount.id}
                  </div>
                )}

                <div className="mt-4">
                  {!assignedRep ? (
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                      You don't have an assigned representative yet. Please contact support.
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-700 text-sm font-semibold flex items-center justify-center">
                        {getInitials(getDisplayName(assignedRep))}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900 truncate">
                          {assignedRep.fullName || assignedRep.displayName || "Assigned Rep"}
                        </div>
                        <div className="text-xs text-slate-600 truncate">
                          {assignedRep.email || "—"}
                        </div>
                        {assignedRep.phone ? (
                          <div className="text-xs text-slate-600 truncate">{assignedRep.phone}</div>
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-[11px] text-slate-500">
                Need help? Ask your rep to invite additional portal users for your account.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // STAFF VIEW (tabs)
  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden mb-6">
      <div className="border-b border-slate-200">
        <nav className="flex flex-wrap">
          <button
            type="button"
            onClick={() => setActiveTab(TABS.INVITES)}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
              activeTab === TABS.INVITES
                ? "text-blue-600 bg-blue-50"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
          >
            <UserPlus className="w-5 h-5" />
            <span>Invites</span>
            {activeTab === TABS.INVITES && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab(TABS.GROUPS)}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
              activeTab === TABS.GROUPS
                ? "text-blue-600 bg-blue-50"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
            title={!isAdminView ? "Read-only for End Users (admin/owner manage changes)." : ""}
          >
            <Users className="w-5 h-5" />
            <span>Groups</span>
            {activeTab === TABS.GROUPS && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab(TABS.TERRITORIES)}
            className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-all relative ${
              activeTab === TABS.TERRITORIES
                ? "text-blue-600 bg-blue-50"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            }`}
            title={!isAdminView ? "Read-only for End Users (admin/owner manage changes)." : ""}
          >
            <MapIcon className="w-5 h-5" />
            <span>Territories</span>
            {activeTab === TABS.TERRITORIES && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600" />
            )}
          </button>
        </nav>
      </div>

      <div className="p-6 space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 flex-1">{error}</p>
          </div>
        )}

        {activeTab === TABS.INVITES && isAdminView ? (
          <div className="rounded-2xl bg-gradient-to-r from-slate-50 via-white to-sky-50/80 px-4 py-3 shadow-sm ring-1 ring-slate-200/80">
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="mr-2 rounded-2xl bg-white/75 px-3 py-2 ring-1 ring-slate-200/70">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Company
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-900">{companyName}</div>
              </div>

              <SeatPill
                label="Admin seats"
                used={seatSummary.admin.used}
                effectiveLimit={seatSummary.admin.effectiveLimit}
                left={seatSummary.admin.left}
                gift={seatSummary.admin.gift}
              />
              <SeatPill
                label="Team seats"
                used={seatSummary.endUser.used}
                effectiveLimit={seatSummary.endUser.effectiveLimit}
                left={seatSummary.endUser.left}
                gift={seatSummary.endUser.gift}
              />

              <div className="ml-auto text-[11px] text-slate-500">
                Seats count only <span className="font-semibold text-slate-700">Active</span> staff.
              </div>
            </div>
          </div>
        ) : null}

        {/* ✅ REMOVED: The 3 boxes (active, inactive, customer) - only invites list remains */}

        {activeTab === TABS.INVITES && (
          <Invites
            companyId={companyId}
            currentUserDoc={currentUserDoc}
            company={company}
            inviteForm={inviteForm}
            setInviteForm={setInviteForm}
            handleInviteUser={handleInviteUser}
            inviting={inviting}
            pendingInvites={pendingInvites}
            busyInviteId={busyInviteId}
            onCancelInvite={handleCancelInvite}
            onResendInvite={handleResendInvite}
            accounts={accounts}
            error={error}
            onAccountSelect={handleAccountSelect}
            onOpenSelectedAccount={handleOpenSelectedAccount}
            accountSelected={accountSelected}
            firebaseUser={firebaseUser}
            isAdminView={isAdminView}
            canInviteTeamMembers={canInviteTeamMembers}
            canInviteAdmins={canInviteAdmins}
            canManageRoleChanges={canManageRoleChanges}
            canDeactivateUsers={canDeactivateStaff}
            companyUsers={teamUsersEnriched}
            seats={seatsForInvites}
            checkingCustomerEmail={checkingCustomerEmail}
            existingCustomerUserByEmail={existingCustomerUserByEmail}
            pendingCustomerInviteByEmail={pendingCustomerInviteByEmail}
            // ✅ FIX: Pass categorized user lists to Invites
            activeUsers={inviteScopedLists.activeUsers}
            inactiveUsers={inviteScopedLists.inactiveUsers}
            customers={inviteScopedLists.customerUsers}
            userAssignmentStats={userAssignmentStats}
            onDeactivateUser={(user) => handleSetStaffStatus(user, "Inactive")}
            onReactivateUser={(user) => handleSetStaffStatus(user, "Active")}
            onChangeStaffRole={handleChangeStaffRole}
          />
        )}

        {activeTab === TABS.GROUPS && (
          <Groups
            currentUserDoc={currentUserDoc}
            firebaseUser={firebaseUser}
            teamUsers={staffUsersOnly}
            onRefreshTeamUsers={() => loadTeamUsers(companyId, currentUserDoc, firebaseUser?.uid)}
            onGroupsChanged={() => {}}
            isAdminView={isAdminView}
          />
        )}

        {activeTab === TABS.TERRITORIES && (
          <Territories
            currentUserDoc={currentUserDoc}
            firebaseUser={firebaseUser}
            teamUsers={staffUsersOnly}
            onRefreshTeamUsers={() => loadTeamUsers(companyId, currentUserDoc, firebaseUser?.uid)}
            isAdminView={isAdminView}
          />
        )}
      </div>
    </div>
  );
};

export default TeamScreen;
